import classNames from 'classnames';
import React from 'react';
import { BUTTON_BOX_CLASS, BUTTON_TYPOGRAPHY_CLASS } from 'ud-ui/components/button/button-metrics';
import UDText, { UDTextType, UDTextVariant } from 'ud-ui/components/text';
import { SKELETON_NBSP } from './skeleton-tokens';

/**
 * Плашки скелетона. Правило у всех одно: размер плашки задаёт **та же величина, что и в бою**,
 * а не подобранное число. Высоту текста — `line-height` того же типа `UDText`, ширину кнопки или
 * бейджа — их собственная подпись под теми же классами, только прозрачная.
 */

interface UDSkeletonTextProps {
  /** Тип `UDText` текста, на месте которого стоит плашка. */
  type: UDTextType;
  /** Ширина плашки (`w-48`): среднее по реальным данным. */
  className: string;
}

/** Плашка на месте строки текста: блочный `UDText` того же типа. */
export const UDSkeletonText = ({ type, className }: UDSkeletonTextProps) => (
  <UDText type={type} className={classNames('skeleton-block', className)} aria-hidden>
    {SKELETON_NBSP}
  </UDText>
);

/**
 * Плашка внутри чужого текстового узла — например, в `title` / `subtitle` у `UDEntityRow`,
 * который сам оборачивает значение в `UDText` нужного типа.
 */
export const UDSkeletonInline = ({ className }: { className: string }) => (
  <span className={classNames('skeleton-block inline-block max-w-full', className)} aria-hidden>
    {SKELETON_NBSP}
  </span>
);

interface UDSkeletonLabelProps {
  /** Подпись, которая будет на этом месте: она даёт ширину и не видна. */
  label: string;
  /** Классы боевого элемента — высота, поля, скругление. */
  className: string;
  /** Вариант `UDText` боевого элемента, если подпись в нём набрана через `UDText variant`. */
  variant?: UDTextVariant;
}

/**
 * Плашка по подписи: бейдж, пилюля статуса, любой элемент, ширину которого задаёт текст.
 * Передайте классы и вариант текста боевого элемента — плашка совпадёт с ним по размеру.
 */
export const UDSkeletonLabel = ({ label, className, variant }: UDSkeletonLabelProps) => {
  const classes = classNames('skeleton-block whitespace-nowrap text-transparent', className);

  return variant ? (
    <UDText as="span" variant={variant} className={classes} aria-hidden>
      {label}
    </UDText>
  ) : (
    <span className={classes} aria-hidden>
      {label}
    </span>
  );
};

/**
 * Плашка на месте `UDButton`: та же типографика, высота, поля и рамка (`button-metrics.ts`).
 * `className` — тот же, что у живой кнопки, когда её ширина задана не подписью (фиксированная колонка действий).
 */
export const UDSkeletonButton = ({ label, className }: { label: string; className?: string }) => (
  <UDSkeletonLabel
    label={label}
    className={classNames('flex shrink-0 items-center border-transparent', BUTTON_BOX_CLASS, BUTTON_TYPOGRAPHY_CLASS, className)}
  />
);
